import React, { useState } from "react";
import { Play } from "lucide-react";

export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  channelTitle: string;
  publishedAt: string;
  viewCount?: string;
  duration?: string;
  url: string;
}

export interface YouTubeChannel {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  subscriberCount?: string;
  videoCount?: string;
  url: string;
}

export interface YouTubeSearchResult {
  videos: YouTubeVideo[];
  channels: YouTubeChannel[];
  totalResults: number;
  query: string;
}

interface YouTubeResultsProps {
  data: YouTubeSearchResult;
  query: string;
  isDarkMode?: boolean;
}

export const YouTubeResults: React.FC<YouTubeResultsProps> = ({ data, query, isDarkMode }) => {
  const { videos, channels } = data || { videos: [], channels: [] };
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  if (!videos?.length && !channels?.length) {
    return (
      <div
        data-isdarkmode={isDarkMode ? "true" : "false"}
        className={`rounded-2xl border p-6 text-center my-4 ${isDarkMode ? "bg-[#1c1c1c] border-gray-700 text-white" : "bg-gray-50 border-gray-200 text-gray-900"
          }`}
      >
        <h2 className="text-xl font-semibold mb-2">📺 No YouTube Results Found</h2>
        <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
          No videos or channels found for "{query}". Try different keywords!
        </p>
      </div>
    );
  }

  return (
    <div data-isdarkmode={isDarkMode ? "true" : "false"} className="space-y-6">
      {videos?.length > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {videos.map((video, index) => {
              const isPlaying = playingVideoId === video.id;

              return (
                <div
                  key={video.id}
                  data-isdarkmode={isDarkMode ? "true" : "false"}
                  className={`group rounded-xl overflow-hidden border ${isDarkMode ? "bg-[#121212] border-[#202020]" : "bg-white border-gray-200"
                    }`}
                >
                  <div className="relative bg-black w-full h-[200px]">
                    {isPlaying ? (
                      <iframe
                        title={video.title}
                        src={`https://www.youtube.com/embed/${video.id}?autoplay=1&enablejsapi=1&controls=1&modestbranding=1&rel=0`}
                        className="w-full h-full border-0 block"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div
                        className="w-full h-full relative cursor-pointer group/image"
                        onClick={() => setPlayingVideoId(video.id)}
                      >
                        <img
                          src={video.thumbnail || `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover opacity-80 group-hover/image:opacity-100 transition-opacity"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-black/50 rounded-full p-4 group-hover/image:bg-red-600 transition-colors">
                            <Play className="w-8 h-8 text-white fill-white" />
                          </div>
                        </div>
                        {video.duration ? (
                          <span className="absolute bottom-2 right-2 text-[11px] font-semibold text-white bg-black/60 px-1.5 py-0.5 rounded-md">
                            {video.duration}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h4
                      className={`text-[14px] font-semibold leading-tight line-clamp-2 ${isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {video.title}
                    </h4>
                    <div
                      className={`flex items-center justify-between text-[11px] mt-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                    >
                      <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                        {video.channelTitle}
                      </p>
                      <span>{video.viewCount || "Unknown views"}</span>
                    </div>
                    <div
                      className={`flex items-center justify-between text-[11px] mt-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                    >
                      <span>
                        {new Date(video.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                      </span>
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          type="button"
                          className={`text-xs px-2 py-1 rounded-xl border transition-colors ${isDarkMode
                              ? "border-[#2c2c2c] text-white hover:bg-[#303030]"
                              : "border-gray-300 text-gray-800 hover:bg-gray-100"
                            }`}
                          onClick={() => {
                            const event = new CustomEvent("summarizeVideo", {
                              detail: { url: `https://www.youtube.com/watch?v=${video.id}` },
                            });
                            window.dispatchEvent(event);
                          }}
                        >
                          📝 Summarize
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {channels?.length > 0 && (
        <div className="space-y-3">
          <h3 className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            👤 Channels ({channels.length})
          </h3>
          <div
            data-isdarkmode={isDarkMode ? "true" : "false"}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4"
          >
            {channels.map((channel) => (
              <div
                key={channel.id}
                data-isdarkmode={isDarkMode ? "true" : "false"}
                className={`rounded-xl p-4 border ${isDarkMode ? "bg-[#121212] border-gray-700" : "bg-white border-gray-200"
                  }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={channel.thumbnail}
                    alt={channel.title}
                    className="w-12 h-12 rounded-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                    }}
                  />
                  <div className="min-w-0">
                    <h4 className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                      {channel.title}
                    </h4>
                    <p className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                      {channel.subscriberCount || "Unknown subscribers"}
                    </p>
                  </div>
                </div>
                <p className={`text-[13px] leading-snug line-clamp-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  {channel.description || "No description available"}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeResults;
